import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import type { TeamPlayer } from "../../types";
import BenchGrid from "./BenchGrid";
import NotInSquadZone from "./NotInSquadZone";
import StartersArea, { type LineupEditingState } from "./StartersArea";
import {
  useLineupDnd,
  MAX_LINEUP_STARTERS,
  MAX_LINEUP_SUBSTITUTES,
  MAX_MATCHDAY_SQUAD,
  type LineupPlayerSelection,
  type LineupZone,
} from "./useLineupDnd";

export type { LineupEditingState } from "./StartersArea";

interface LineupBoardProps {
  teamType: "home" | "away";
  candidates: TeamPlayer[];
  lineup: LineupPlayerSelection[];
  onLineupChange: (lineup: LineupPlayerSelection[]) => void;
  readOnly: boolean;
  editingPlayer: LineupEditingState | null;
  onStartEdit: (
    teamType: "home" | "away",
    player: TeamPlayer,
    zone: LineupZone,
  ) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditJerseyChange: (value: number) => void;
  onEditNameChange: (value: string) => void;
  /** Opens the existing QuickCreatePlayerModal for this team (bolita
   * placeholder chips at the end of the bench strip). Omit to keep the
   * board exactly as before — no placeholders render without it. */
  onOpenQuickCreate?: () => void;
  /** PMA-COCK-171: the live in-cockpit bench panel — starters are locked
   * (no move to/from titulares in either direction) while bench and
   * not-in-squad stay fully editable. `false`/omitted keeps the pre-match
   * board's unrestricted behavior. */
  benchOnly?: boolean;
  /** PMA-COCK-171: bench player ids that cannot be moved at all (already
   * entered the field via a Substitution event). Ignored unless the player
   * is currently on the bench. */
  benchLockedPlayerIds?: Set<string>;
  t: TFunction;
}

/** Bolita placeholder chips are capped low — they're a quick "ad-hoc
 * player" affordance, not a bulk-add tool — and further capped by
 * whatever matchday-squad headroom remains. */
const MAX_PLACEHOLDER_CHIPS = 3;

/**
 * Drag-and-drop lineup board (EPIC-08 Phase 3). Three zones, laid out side
 * by side at tablet width and up (client-requested density reflow, new UC
 * pending ID assignment at wave synthesis — "una sola pantalla para ver
 * todo": operator needs starters, substitutes AND not-in-squad visible
 * together, not one below the other requiring scroll to compare counts):
 * a starters area (cap 11), a bench
 * grid (cap 12), and a "not in matchday squad" / "fuera de la convocatoria"
 * zone, expanded by default so it is visible alongside the other two.
 *
 * Every roster candidate starts in the not-in-squad zone and must be
 * explicitly called up (client request 2026-08-18). Only players with an
 * entry in `lineup` are saved; the zone is not a client-only annotation any
 * more, it is simply "has no lineup entry". Both caps are hard — a move
 * past one is rejected inline and the player stays where they were.
 * Read-only while the match is not Pending
 * (kickoff locks the lineup server-side; this mirrors that client-side so
 * users see a locked board instead of a silent save rejection).
 */
export default function LineupBoard({
  teamType,
  candidates,
  lineup,
  onLineupChange,
  readOnly,
  editingPlayer,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditJerseyChange,
  onEditNameChange,
  onOpenQuickCreate,
  benchOnly = false,
  benchLockedPlayerIds,
  t,
}: LineupBoardProps) {
  // Defaults to expanded (was collapsed) so all three zones are visible
  // together without an extra tap — density reflow, see the file doc
  // comment above. Still toggleable for a long "not in squad" list.
  const [manuallyExpanded, setManuallyExpanded] = useState(true);
  // Memoised so the object identity is stable across renders — `assign`
  // lists it as a dependency, and a fresh literal each render would rebuild
  // every chip's handlers on every keystroke in the inline jersey editor.
  const capMessages = useMemo(
    () => ({
      starters: t("lineupBoard.capReached", {
        count: MAX_LINEUP_STARTERS,
        defaultValue: "Cannot select more than {{count}} starters",
      }),
      substitutes: t("lineupBoard.substitutesCapReached", {
        count: MAX_LINEUP_SUBSTITUTES,
        defaultValue: "Cannot select more than {{count}} substitutes",
      }),
    }),
    [t],
  );
  const dnd = useLineupDnd({
    candidates,
    lineup,
    disabled: readOnly,
    onChange: onLineupChange,
    capMessages,
    benchOnly,
    lockedPlayerIds: benchLockedPlayerIds,
  });

  const squadSize = dnd.starters.length + dnd.bench.length;
  const overCap = squadSize > MAX_MATCHDAY_SQUAD;
  const isNotInSquadExpanded = manuallyExpanded || overCap;
  const remainingSquadSlots = Math.max(0, MAX_MATCHDAY_SQUAD - squadSize);
  const placeholderCount = readOnly
    ? 0
    : Math.min(MAX_PLACEHOLDER_CHIPS, remainingSquadSlots);

  return (
    <div className="space-y-3">
      {readOnly && (
        <p
          className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2"
          data-testid={`${teamType}-lineup-locked-hint`}
        >
          {t("lineupBoard.locked", {
            defaultValue:
              "Lineup is locked because this match is no longer Pending.",
          })}
        </p>
      )}
      {!readOnly && benchOnly && (
        <p
          className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2"
          data-testid={`${teamType}-lineup-bench-only-hint`}
        >
          {t("lineupBoard.benchOnlyHint", {
            defaultValue:
              "Starters are locked while the match is live. You can add players to the bench or remove a substitute who has not entered.",
          })}
        </p>
      )}
      {dnd.capError && (
        <p
          role="alert"
          className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2"
          data-testid={`${teamType}-lineup-cap-error`}
        >
          {dnd.capError}
        </p>
      )}
      {overCap && (
        <p
          role="alert"
          className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2"
          data-testid={`${teamType}-squad-cap-hint`}
        >
          {t("lineupBoard.squadCapHint", {
            count: squadSize - MAX_MATCHDAY_SQUAD,
            max: MAX_MATCHDAY_SQUAD,
            defaultValue:
              'Matchday squad is limited to {{max}}; move {{count}} player(s) to "Not in matchday squad".',
          })}
        </p>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-start">
        <StartersArea
          teamType={teamType}
          chips={dnd.starters}
          dnd={dnd}
          readOnly={readOnly || benchOnly}
          editingPlayer={editingPlayer}
          onStartEdit={(team, player) => onStartEdit(team, player, "starters")}
          onCancelEdit={onCancelEdit}
          onSaveEdit={onSaveEdit}
          onEditJerseyChange={onEditJerseyChange}
          onEditNameChange={onEditNameChange}
          t={t}
        />
        <BenchGrid
          teamType={teamType}
          chips={dnd.bench}
          dnd={dnd}
          readOnly={readOnly}
          editingPlayer={editingPlayer}
          onStartEdit={(team, player) => onStartEdit(team, player, "bench")}
          onCancelEdit={onCancelEdit}
          onSaveEdit={onSaveEdit}
          onEditJerseyChange={onEditJerseyChange}
          onEditNameChange={onEditNameChange}
          placeholderCount={placeholderCount}
          onOpenQuickCreate={onOpenQuickCreate}
          benchOnly={benchOnly}
          lockedPlayerIds={benchLockedPlayerIds}
          t={t}
        />
        <NotInSquadZone
          teamType={teamType}
          chips={dnd.excluded}
          dnd={dnd}
          readOnly={readOnly}
          expanded={isNotInSquadExpanded}
          onToggleExpanded={() => setManuallyExpanded((prev) => !prev)}
          editingPlayer={editingPlayer}
          onStartEdit={(team, player) => onStartEdit(team, player, "excluded")}
          onCancelEdit={onCancelEdit}
          onSaveEdit={onSaveEdit}
          onEditJerseyChange={onEditJerseyChange}
          onEditNameChange={onEditNameChange}
          t={t}
        />
      </div>
    </div>
  );
}
